/**
 * 管理分页编辑器导航的排序会话，隔离拖拽选中状态与画布激活副作用
 * 依赖导航事件及 Frame、Stage 服务，画布切换只在拖拽结束后执行
 */
import type { DragStart, DropResult } from '@hello-pangea/dnd';
import { difference, range } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import type { Editor } from '@meitu/saas-editor-core';
import type { EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import { IS_DRAGGING_EVENT } from '../event-name';

/**
 * 在页面导航中挂载排序处理，保存拖拽开始时的选择与激活页
 *
 * 切换画布会平移视口并触发合成 mousedown；启动拖拽时切换会中断传感器，
 * 因此完成排序后再激活画布，取消时恢复原选择。
 * 更新导航选择；成功放置后调整画布顺序、激活页及滚动位置。
 *
 * @param editor - 提供画布选择、排序与历史记录的编辑器实例
 * @param events - 同步导航页项拖拽样式的事件总线
 * @param setSelection - 更新导航多选状态及 Frame 选择的回调
 * @returns 拖拽状态及传给 DragDropContext 的开始、结束回调
 */
export function useStageDrag(editor: Editor, events: EventEmitter, setSelection: (indexes: number[]) => void) {
    const [onDragging, setOnDragging] = useState(false);
    const sessionRef = useRef<{
        selection: number[];
        previousSelection: number[];
        activeIndex: number;
    } | null>(null);

    const onBeforeDragStart = useCallback(
        ({ source }: DragStart) => {
            const previousSelection = [...editor.services.frame.getFrameSelection()];
            const isSelected = previousSelection.includes(source.index);
            const selection = isSelected ? previousSelection : [source.index];
            sessionRef.current = {
                selection,
                previousSelection,
                activeIndex: isSelected ? editor.services.frame.getActiveIndex() : source.index,
            };
            setOnDragging(true);
            events.emit(IS_DRAGGING_EVENT, source.index);
            if (!isSelected) setSelection(selection);
        },
        [editor, events, setSelection],
    );

    const moveCard = useCallback(
        ({ destination, source, reason }: DropResult) => {
            const session = sessionRef.current;
            sessionRef.current = null;
            events.emit(IS_DRAGGING_EVENT, -1);
            setOnDragging(false);
            if (!session) return;
            if (reason !== 'DROP' || !destination) {
                setSelection(session.previousSelection);
                return;
            }

            const { selection, activeIndex } = session;
            const selectionRange = range(selection[0], selection[selection.length - 1] + 1);
            const excludeRange = difference(selectionRange, selection);
            if (
                (destination.index === source.index && selection.length === 1) ||
                (selection.includes(destination.index) && !excludeRange.length)
            ) {
                editor.services.frame.setActiveIndex(activeIndex);
                editor.services.frame.scrollToActiveFrame();
                return;
            }

            const { newStageSelection } = editor.services.stage.swapStages(
                selection,
                destination.index,
                source.index,
                activeIndex,
            );
            editor.services.frame.scrollToActiveFrame();
            if (newStageSelection) setSelection(newStageSelection);
        },
        [editor, events, setSelection],
    );

    return { onDragging, onBeforeDragStart, moveCard };
}
